import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Grid,
  CircularProgress,
  Backdrop,
  MenuItem,
  Box,
} from '@mui/material';
import { ExitToApp } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { Auth } from '../services/amplifyCompat';
import { connect } from 'react-redux';
import { updateLoginState } from '../actions/loginActions';
import { updateMenuState } from '../actions/menuActions';
import LanguageHandler from './LanguageHandler';
import useTranslation from './customHooks/translations';
import { getUserEmail } from '../utils/signup-flow-type';

const useStyles = makeStyles()((theme) => {
  return {
    grow: {
      flexGrow: 1,
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      position: 'sticky',
      top: '0',
    },
    topBar: {
      zIndex: theme.zIndex.drawer + 1,
      background: '#FFFFFF',
      boxShadow: 'none',
      color: '#0F172A',
      borderBottom: '1px solid #E2E8F0',
      '.MuiOutlinedInput-notchedOutline': {
        borderColor: '#E2E8F0',
      },
      '.MuiSelect-icon': {
        color: '#64748B',
      },
    },
    languageLabel: {
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'inline',
      },
    },
    menuButton: {
      color: '#64748B',
      marginRight: 5,
      borderRadius: '8px',
      [theme.breakpoints.up('sm')]: {
        marginRight: 16,
      },
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
      '&:hover': {
        backgroundColor: '#F1F5F9',
      },
    },
    title: {
      fontSize: '1.1em',
      fontWeight: '700',
      fontFamily: '"Outfit", "DM Sans", sans-serif',
      color: '#0D9488',
      letterSpacing: '-0.02em',
      [theme.breakpoints.up('sm')]: {
        display: 'block',
        fontSize: '1.2em',
      },
    },
    avatar: {
      background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
      textTransform: 'uppercase',
      fontFamily: '"Outfit", "DM Sans", sans-serif',
      fontWeight: 600,
      fontSize: '0.95rem',
      width: 36,
      height: 36,
      cursor: 'pointer',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      '&:hover': {
        transform: 'scale(1.05)',
        boxShadow: '0 2px 8px rgba(13,148,136,0.3)',
      },
    },
    logo: {
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'block',
      },
      paddingLeft: '15px',
    },
    sectionDesktop: {
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
    },
    sectionMobile: {
      display: 'flex',
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#0D9488',
    },
    logOut: {
      color: '#64748B',
      borderRadius: '8px',
      fontSize: '0.9rem',
      transition: 'all 0.15s ease',
      '&:hover': {
        color: '#DC2626',
        backgroundColor: '#FEF2F2',
      },
    },
  };
});

function Navbar(props) {
  const {
    updateLoginState,
    updateMenuState,
    loginState,
    menuEnabled,
    showSideMenuButton,
  } = props;

  const { classes } = useStyles();

  const navigate = useNavigate();
  const translation = useTranslation();

  const [user, setUser] = useState('');
  const [loadingBackdrop, setLoadingBackdrop] = useState(false);

  const handleProfileMenuOpen = () => {
    navigate('/account-settings');
  };

  const handleLogout = async () => {
    setLoadingBackdrop(true);
    await new Promise((r) => setTimeout(r, 1000));
    await onSignOut();
    setLoadingBackdrop(false);
  };

  useEffect(() => {
    async function retrieveUser() {
      try {
        const returnedUser = await Auth.currentAuthenticatedUser();
        setUser(getUserEmail(returnedUser));
      } catch {
        setUser('');
      }
    }
    retrieveUser();
  }, [loginState]);

  const handleSideMenu = () => {
    updateMenuState(!menuEnabled);
  };

  async function onSignOut() {
    updateLoginState('signIn');
    navigate('/');
    await Auth.signOut();
  }

  return (
    <Grid item xs={12} className={classes.appBar}>
      <AppBar position="fixed" className={classes.topBar}>
        <Toolbar>
          {showSideMenuButton ? (
            <IconButton
              edge="start"
              className={classes.menuButton}
              color="inherit"
              aria-label="Toggle menu drawer"
              onClick={handleSideMenu}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box
            component="img"
            sx={{
              height: 32,
              width: 32,
              marginRight: 1,
            }}
            alt=""
            src="/icon-192x192.png"
          />

          <Typography
            className={classes.title}
            variant="h6"
            component={'span'}
            noWrap
          >
            Commit2Act
          </Typography>

          <div className={classes.grow} />
          <div className={classes.sectionDesktop}>
            <div>
              <LanguageHandler />
            </div>
            <MenuItem className={classes.logOut} onClick={handleLogout}>
              <span>{translation.logout} </span>
              <ExitToApp fontSize="small" />
            </MenuItem>
            <IconButton
              edge="end"
              aria-label="account of current user"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar className={classes.avatar}>
                {user.charAt(0)}
              </Avatar>
            </IconButton>
          </div>
          <div className={classes.sectionMobile}>
            <Avatar className={classes.avatar} onClick={handleProfileMenuOpen}>
              {user.charAt(0)}
            </Avatar>
          </div>
        </Toolbar>
      </AppBar>
      <Backdrop className={classes.backdrop} open={loadingBackdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Grid>
  );
}

const mapStateToProps = (state) => {
  return {
    loginState: state.loginState.currentState,
    menuEnabled: state.appState.showSideBar,
  };
};

const mapDispatchToProps = {
  updateLoginState,
  updateMenuState,
};

export default connect(mapStateToProps, mapDispatchToProps)(Navbar);
